import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { compose, branch, renderComponent } from 'recompose'
import cx from 'classnames'
import { Div, H1, Button } from '@driftt/tide-core'

import { isExtensionInstalled, isChrome } from 'utils/extension'
import { page, getVideoSignupReferrer } from 'utils/trackingUtils'
import config from 'config'

import gradientArrowLeft from './gradient-arrow-left.svg'
import boltTrioTeal from './bolt-trio-teal.svg'
import boltTrioPink from './bolt-trio-pink.svg'

import PoweredByInstallVariant from './variants/powered-by-install-component'
import AppSwitcherInstallVariant from './variants/app-switcher-install-component'
import MarketingInstallVariant from './variants/marketing-install-component'

import './style.css'

export const InstallButton = ({ onInstallClick, copy, showArrow = false }) => (
  <div className="drift-install-CTA-container">
    {showArrow && (
      <img className="drift-install-CTA-arrow" src={gradientArrowLeft} alt="arrow" />
    )}
    <Button className="drift-install-CTA" onClick={onInstallClick} size="large">
      {copy}
    </Button>
  </div>
)

const ExtensionInstallOverlay = ({ showInstallOverlay, retryInstall, dismissInstall }) => (
  <div
    className={cx('drift-install-overlay-container', { 'show-overlay': showInstallOverlay })}
    id="drift-install-overlay-container"
  >
    <div className="drift-install-overlay-left">
      <span className="drift-install-retry-copy">
        Trouble installing? Try this!{' '}
        <span role="img" aria-label="pointing right">
          👉
        </span>
      </span>
      <div className="drift-install-retry">
        <Button className="drift-install-CTA" onClick={retryInstall}>
          Retry installation
        </Button>
        <Div className="drift-install-dismiss" onClick={dismissInstall}>
          Not now
        </Div>
      </div>
    </div>
    <div className="drift-install-overlay-right">
      <img className="drift-install-arrow" src={gradientArrowLeft} alt="arrow" />
      <div className="drift-getting-started-title">Click to add Drift Video</div>
      <img className="drift-bolt-trio-right" src={boltTrioTeal} alt="bolts-teal" />
    </div>
  </div>
)

export const NonChromeInstallStep = ({ name }) => (
  <div className="video-install-container">
    <div className="drift-getting-started">
      <div className="drift-install-content">
        <img className="drift-bolt-trio-left" src={boltTrioPink} alt="bolts-pink" />
        <Div className="drift-getting-started-microcopy">Welcome{name && `, ${name}`}!</Div>
        <H1 className="drift-getting-started-title">
          Add Drift Video to Chrome and combine the power of chat and video in conversations.
        </H1>
        <Div className="drift-getting-started-copy">
          With Drift Video, you can chat with prospects in real-time while they watch your video. And if you&rsquo;re not around, no problem &mdash; they can book a meeting with you right from the video.
        </Div>
        <Button className="drift-install-CTA" href={config.DRIFT_VIDEO_EXTENSION_URL} target="_blank">
          Visit Chrome store
        </Button>
      </div>
      <a href="https://www.drift.com/video/?wvideo=lkssyh2pqb" target="_blank" rel="noopener noreferrer">
        <img
          className="drift-install-nonchrome-video"
          alt="drift video content for users with other browsers"
          src="https://embedwistia-a.akamaihd.net/deliveries/35872b79344d19eba48d0c87886a75b7.jpg?image_play_button_size=2x&amp;image_crop_resized=960x540&amp;image_play_button=1&amp;image_play_button_color=111111e0"
        />
      </a>
    </div>
  </div>
)

class VideoInstallStep extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      showInstallOverlay: false,
    }
  }

  componentDidMount() {
    page('Video Extension Install Viewed', {
      source: getVideoSignupReferrer()
    })
  }

  launchWebStore = () => {
    const height = window.innerHeight + window.innerHeight * 0.12
    const width = window.innerWidth / 2

    window.open(
      config.DRIFT_VIDEO_EXTENSION_URL,
      'Drift Video Extension',
      `location=1,directories=1,toolbar=1,titlebar=1,resizeable=1,scrollbars=1,status=1,left=${
        window.screenX
      },top=${window.screenY},width=${width},height=${height}`
    )

    const popupTick = setInterval(async () => {
      const installed = await isExtensionInstalled()
      if (installed) {
        clearInterval(popupTick)
        this.props.actions.push('/video/success')
      }
    }, 1000)
  }

  onInstallClick = () => {
    this.setState({ showInstallOverlay: true })
    this.launchWebStore()
  }

  onRetryInstallClick = () => {
    this.launchWebStore()
  }

  onDismissInstallClick = () => {
    this.setState({ showInstallOverlay: false })
  }

  getInstallVariation = () => {
    const { installVariant } = this.props

    if (installVariant === 'powered-by') {
      return <PoweredByInstallVariant onInstallClick={this.onInstallClick} />
    } else if (installVariant === 'app-switcher') {
      return <AppSwitcherInstallVariant onInstallClick={this.onInstallClick} />
    } else {
      return <MarketingInstallVariant onInstallClick={this.onInstallClick} />
    }
  }

  render() {
    return (
      <div className="video-install-container">
        {this.getInstallVariation()}
        <ExtensionInstallOverlay 
          showInstallOverlay={this.state.showInstallOverlay} 
          retryInstall={this.onRetryInstallClick} 
          dismissInstall={this.onDismissInstallClick}
        />
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    push,
  }, dispatch)
})

export default compose(
  connect(null, mapDispatchToProps),
  branch(props => !isChrome, renderComponent(NonChromeInstallStep))
)(VideoInstallStep)
